import {
  getGraphDefinition,
  getGraphMotivations,
} from 'affiny-core/helpers/personalityReport';
import { IPersonalityReportGraph } from 'affiny-core/modules/personalityReport/types';
import { Box, rem, Text } from 'affiny-ui/src';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PersonalityReportLayerValuesDetailedValue } from './PersonalityReportLayerValuesDetailedValue';
import { PersonalityReportLayerParagraph } from './PersonalityReportLayerParagraph';

interface IPersonalityReportLayerValuesSubChapter {
  graph: IPersonalityReportGraph;
  setTitle: (title: string) => void;
}

export const PersonalityReportLayerValuesSubChapter = ({
  graph,
  setTitle,
}: IPersonalityReportLayerValuesSubChapter) => {
  const { t } = useTranslation();

  const definition = useMemo(() => getGraphDefinition(graph), [graph]);
  const motivations = useMemo(() => getGraphMotivations(graph), [graph]);

  const isMotivationsCorrect = useMemo(
    () =>
      motivations.length >= 3 &&
      motivations.every(motivation => typeof motivation === 'string'),
    [motivations]
  );

  return (
    <Box pb="xLarge">
      <Box marginBottom="xLarge">
        <PersonalityReportLayerValuesDetailedValue
          label={t(`personalityReport.values.detailedValue.valueLabel`)}
          valueId={graph.id}
          setTitle={setTitle}
        >
          <Text fontStyle="italic" fontSize="size14">
            {t(`personalityReport.values.detailedValue.definition`)}
          </Text>
          <Text fontStyle="italic" fontSize={rem(20)} lineHeight={rem(28)}>
            {definition}
          </Text>
        </PersonalityReportLayerValuesDetailedValue>
      </Box>

      {graph.analysis.map((analysis, idx) => (
        <PersonalityReportLayerParagraph key={`analysis${idx}`}>
          {analysis}
        </PersonalityReportLayerParagraph>
      ))}

      {isMotivationsCorrect && (
        <Text fontSize={rem(18)} fontWeight="600" mb="xLarge">
          {t(`personalityReport.values.detailedValue.motivationsLabel`, {
            label: t(`personalityReport.values.valueLabels.${graph.id}`),
          })}
          {t(`personalityReport.values.detailedValue.threeMotivation`, {
            first: motivations[0].toLowerCase(),
            second: motivations[1].toLowerCase(),
            third: motivations[2].toLowerCase(),
          })}
        </Text>
      )}
    </Box>
  );
};
